/**
 * Parent-order status derived from its legs — the browser-side mirror of the
 * rule the `poll-eapi-trip-status` edge function already applies.
 *
 * Why this exists: the on-demand sync used to promote `orders.status` straight
 * from `orders.eapi_trip_id`. On a multi-leg order that column is only a copy
 * of leg 1, so leg 1 completing marked the whole order delivered while later
 * legs had not started (AOP-HTSH-06110). The parent may only move when ALL of
 * its live legs say so.
 *
 * Pure module — no React, no Supabase.
 */
import { LEG_STATUS_RANK } from "@/lib/leg-status-resolve";

/** Forward-only ranking shared with the leg resolver. */
const RANK = LEG_STATUS_RANK;

/**
 * Next status for the parent order, or null when it should stay put.
 *
 *  - delivered  → every live (non-canceled) leg delivered
 *  - in_transit → any live leg in transit, or some (not all) delivered
 *  - never regresses, never auto-cancels, never touches a terminal parent
 */
export function computeNextParentStatus(
  currentStatus: string | null | undefined,
  legStatuses: ReadonlyArray<string | null | undefined>,
): string | null {
  if (currentStatus === "delivered" || currentStatus === "canceled") return null;
  if (legStatuses.length === 0) return null;

  const live = legStatuses
    .map((s) => String(s ?? "").trim().toLowerCase())
    .filter((s) => s !== "canceled");
  if (live.length === 0) return null;

  // A leg with no status of its own counts as not started — fail closed so an
  // unknown leg can never drag the parent forward.
  const normalized = live.map((s) => s || "booked");

  let next: string | null = null;
  if (normalized.every((s) => s === "delivered")) next = "delivered";
  else if (normalized.some((s) => s === "in_transit" || s === "delivered")) next = "in_transit";
  if (!next) return null;

  const currentRank = RANK[String(currentStatus ?? "")] ?? -1;
  if ((RANK[next] ?? -1) <= currentRank) return null;
  return next;
}

/**
 * Should `nextStatus` be written onto a leg trip's `eapi_status` mirror?
 * Forward-only: upstream sometimes replays an earlier stage. Never `canceled`.
 */
export function shouldAdvanceTripStatus(
  prevStatus: string | null | undefined,
  nextStatus: string | null | undefined,
): boolean {
  // Never mirror an upstream cancel: it is not an ATMS cancel, and writing it
  // would erase the leg's last real stage. Matches the poller's rule.
  if (!nextStatus || nextStatus === "canceled") return false;
  const prev = RANK[String(prevStatus ?? "")] ?? -1;
  const next = RANK[String(nextStatus)] ?? -1;
  return next > prev;
}

/**
 * May the on-demand sync fall back to the order-level `orders.eapi_trip_id`?
 * Only for legacy orders with no `legs` rows at all. On an order with legs
 * that column is just a copy of leg 1's trip, so using it promoted whole
 * multi-leg orders to delivered off leg 1 alone (AOP-DETM-02576, 02404).
 */
export function mayUseOrderLevelTrip(legCount: number): boolean {
  return legCount === 0;
}

/** Split ids into request-sized groups so `.in()` URLs stay under the limit. */
export function chunkIds<T>(ids: ReadonlyArray<T>, size = 100): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}
